import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import type {
  ContractorStatement,
  CostCentre,
  CrewAction,
  CrewActionResult,
  LabourGroupKey,
  LabourReport,
  LabourSettings,
  OpenSegment,
  PlaceTarget,
  Rate,
  RateInput,
  TimesheetCheckRow,
  WorkSegment,
} from '../types/labour';

// ============================================================================
// LABOUR SERVICE — Phase 1 of the harvest & labour plan, /api/field-work.
// Crew mode, time records and hours reports need a farm_jobs supervisor,
// creator or admin seat; rates, settings, statements and every dollar figure
// are admin-only on the server.
// ============================================================================

export interface LabourFilters {
  from: string;
  to: string;
  farm_id?: number;
  labour_provider_id?: number;
  worker_id?: number;
  activity_type_id?: number;
  job_id?: number;
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function download(url: string, filename: string) {
  const blob = await (apiService.get(url, { responseType: 'blob' }) as unknown as Promise<Blob>);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

class LabourService {
  // --------------------------------------------------------------------------
  // Crew mode
  // --------------------------------------------------------------------------

  async getCrew(jobId: number): Promise<OpenSegment[]> {
    const res = await apiService.get<{ open: OpenSegment[] }>(`/field-work/jobs/${jobId}/crew`);
    return unwrap(res).open ?? [];
  }

  async sendCrewActions(jobId: number, actions: CrewAction[]): Promise<{ results: CrewActionResult[]; open: OpenSegment[] }> {
    const res = await apiService.post<{ results: CrewActionResult[]; open: OpenSegment[] }>(
      `/field-work/jobs/${jobId}/crew/actions`, { actions },
    );
    return unwrap(res);
  }

  // --------------------------------------------------------------------------
  // Time records
  // --------------------------------------------------------------------------

  async getSegments(filters: { from?: string; to?: string; job_id?: number; worker_id?: number; open_only?: boolean } = {}): Promise<WorkSegment[]> {
    const res = await apiService.get<WorkSegment[]>(`/field-work/segments${qs(filters)}`);
    return unwrap(res) ?? [];
  }

  async createSegment(input: {
    worker_id: number; job_id?: number | null; target?: PlaceTarget | null; cost_centre_id?: number | null;
    started_at: string; ended_at: string; break_minutes?: number; units_done?: number | null; unit?: string | null; notes?: string | null;
  }): Promise<WorkSegment> {
    return unwrap(await apiService.post<WorkSegment>('/field-work/segments', input));
  }

  async updateSegment(id: number, input: {
    started_at?: string; ended_at?: string | null; break_minutes?: number; units_done?: number | null;
    unit?: string | null; notes?: string | null; target?: PlaceTarget | null; cost_centre_id?: number | null; reason: string;
  }): Promise<WorkSegment> {
    return unwrap(await apiService.patch<WorkSegment>(`/field-work/segments/${id}`, input));
  }

  async voidSegment(id: number, reason: string): Promise<WorkSegment> {
    return unwrap(await apiService.post<WorkSegment>(`/field-work/segments/${id}/void`, { reason }));
  }

  // --------------------------------------------------------------------------
  // Cost centres & settings
  // --------------------------------------------------------------------------

  async getCostCentres(includeInactive = false): Promise<CostCentre[]> {
    return unwrap(await apiService.get<CostCentre[]>(`/field-work/cost-centres${qs({ include_inactive: includeInactive || undefined })}`)) ?? [];
  }

  async saveCostCentre(id: number | null, input: { name?: string; code?: string | null; farm_id?: number | null; active?: boolean }): Promise<CostCentre> {
    return unwrap(id == null
      ? await apiService.post<CostCentre>('/field-work/cost-centres', input)
      : await apiService.patch<CostCentre>(`/field-work/cost-centres/${id}`, input));
  }

  async getSettings(): Promise<{ settings: LabourSettings }> {
    return unwrap(await apiService.get<{ settings: LabourSettings }>('/field-work/labour-settings'));
  }

  async saveSettings(input: Partial<LabourSettings>): Promise<LabourSettings> {
    return unwrap(await apiService.put<LabourSettings>('/field-work/labour-settings', input));
  }

  // --------------------------------------------------------------------------
  // Rates
  // --------------------------------------------------------------------------

  async getRates(includeEnded = false): Promise<Rate[]> {
    return unwrap(await apiService.get<Rate[]>(`/field-work/rates${qs({ include_ended: includeEnded || undefined })}`)) ?? [];
  }

  async createRate(input: RateInput): Promise<Rate> {
    return unwrap(await apiService.post<Rate>('/field-work/rates', input));
  }

  async endRate(id: number, effectiveTo: string | null): Promise<Rate> {
    return unwrap(await apiService.patch<Rate>(`/field-work/rates/${id}`, { effective_to: effectiveTo }));
  }

  async deleteRate(id: number): Promise<void> {
    unwrap(await apiService.delete(`/field-work/rates/${id}`));
  }

  // --------------------------------------------------------------------------
  // Reports
  // --------------------------------------------------------------------------

  async getLabourReport(filters: LabourFilters, groupBy: LabourGroupKey[]): Promise<LabourReport> {
    return unwrap(await apiService.get<LabourReport>(`/field-work/reports/labour${qs({ ...filters, group_by: groupBy.join(',') })}`));
  }

  async downloadLabourCsv(filters: LabourFilters, groupBy: LabourGroupKey[]): Promise<void> {
    await download(
      `/field-work/reports/labour${qs({ ...filters, group_by: groupBy.join(','), format: 'csv' })}`,
      `labour-${filters.from}-to-${filters.to}.csv`,
    );
  }

  async getTimesheetCheck(from: string, to: string): Promise<TimesheetCheckRow[]> {
    return unwrap(await apiService.get<TimesheetCheckRow[]>(`/field-work/reports/timesheet-check${qs({ from, to })}`)) ?? [];
  }

  async getContractorStatement(providerId: number, from: string, to: string): Promise<ContractorStatement> {
    return unwrap(await apiService.get<ContractorStatement>(
      `/field-work/reports/contractor-statement${qs({ labour_provider_id: providerId, from, to })}`,
    ));
  }

  async downloadContractorStatementCsv(providerId: number, from: string, to: string): Promise<void> {
    await download(
      `/field-work/reports/contractor-statement${qs({ labour_provider_id: providerId, from, to, format: 'csv' })}`,
      `statement-${providerId}-${from}.csv`,
    );
  }
}

const labourService = new LabourService();
export default labourService;
